// Окно просмотра элемента — обёртка над `ElementDetails`. Открывается из
// промпт-бара (слот элемента), из библиотеки (меню карточки) и из истории
// сообщений (по снимку запроса); пикер показывает ту же деталь панелью внутри
// себя, чтобы не вкладывать модалку в модалку.

import { useTranslation } from "react-i18next";

import { cn } from "~/shared/lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/shared/ui/dialog";

import { useElementLightbox } from "../lib/use-element-lightbox";
import { ElementDetails } from "./element-details";
import { ElementLightbox } from "./element-lightbox";

import type { ElementDetailsView } from "../lib/element-detail-fields";

interface ElementDetailsDialogProps {
    /** `null` — показывать нечего (элемент ещё не выбран или уже удалён). */
    view: ElementDetailsView | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function ElementDetailsDialog({ view, open, onOpenChange }: ElementDetailsDialogProps) {
    const { t } = useTranslation(["elements"]);
    const lightbox = useElementLightbox();

    if (!view) return null;

    return (
        // Пока лайтбокс открыт, диалог невидим и не модален — иначе Radix ловит
        // клики лайтбокса как «снаружи» и закрывает окно (см. `ElementLightbox`).
        <Dialog open={open} onOpenChange={onOpenChange} modal={!lightbox.isOpen}>
            <DialogContent
                className={cn("max-w-2xl", lightbox.isOpen && "pointer-events-none opacity-0")}
                onEscapeKeyDown={(event) => {
                    if (lightbox.isOpen) {
                        lightbox.close();
                        event.preventDefault();
                    }
                }}
            >
                <DialogHeader>
                    <DialogTitle>{t("elements:details.title")}</DialogTitle>
                    {/* Пояснение видно только скринридеру: на экране под ним сразу
                        идут имя и поля, и строка «картинки элемента и его поля»
                        ничего не добавляет. Совсем убрать её нельзя — Radix
                        требует описание у диалога. */}
                    <DialogDescription className="sr-only">
                        {t("elements:details.description")}
                    </DialogDescription>
                </DialogHeader>

                <ElementDetails t={t} view={view} onOpenImage={lightbox.open} />

                <ElementLightbox
                    images={view.images}
                    caption={view.name}
                    index={lightbox.index}
                    onClose={lightbox.close}
                />
            </DialogContent>
        </Dialog>
    );
}
